import type { ReactNode } from 'react';

import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import ChatRoundedIcon from '@mui/icons-material/ChatRounded';
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import CheckroomRoundedIcon from '@mui/icons-material/CheckroomRounded';
import CloudSyncRoundedIcon from '@mui/icons-material/CloudSyncRounded';
import LanguageRoundedIcon from '@mui/icons-material/LanguageRounded';
import TranslateRoundedIcon from '@mui/icons-material/TranslateRounded';
import LocalMallRoundedIcon from '@mui/icons-material/LocalMallRounded';
import ShoppingBagRoundedIcon from '@mui/icons-material/ShoppingBagRounded';
import ShoppingCartRoundedIcon from '@mui/icons-material/ShoppingCartRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import SellRoundedIcon from '@mui/icons-material/SellRounded';
import ShoppingCartCheckoutRoundedIcon from '@mui/icons-material/ShoppingCartCheckoutRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import TelegramIcon from '@mui/icons-material/Telegram';
import DomainRoundedIcon from '@mui/icons-material/DomainRounded';
import PermMediaRoundedIcon from '@mui/icons-material/PermMediaRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import PointOfSaleRoundedIcon from '@mui/icons-material/PointOfSaleRounded';
import ShoppingBasketRoundedIcon from '@mui/icons-material/ShoppingBasketRounded';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import StoreRoundedIcon from '@mui/icons-material/StoreRounded';
import WarehouseRoundedIcon from '@mui/icons-material/WarehouseRounded';

import type { PermissionCode } from '@/shared/permissions';
import type { MerchantPortalSettingsSectionKey } from '@/shared/navigation/merchantPortalNavVisibility';

import { STORE_SETTINGS_PERMISSIONS } from '../model/permissions';
import type { StoreSettingsDomain } from '../model/permissions';

export interface SettingsNavItem {
  /** Stable domain key (matches `STORE_SETTINGS_PERMISSIONS`). */
  key: StoreSettingsDomain;
  /** Short menu label. i18next key — render with `t(label)`. */
  label: string;
  /** One-line description for the settings landing grid. i18next key — render with `t(description)`. */
  description: string;
  /** Route segment relative to `/settings`. */
  segment: string;
  /** Absolute route path. */
  path: string;
  /** Section icon. */
  icon: ReactNode;
  /** Permission required to open/view the screen. */
  viewPermission: PermissionCode;
  /** Permission required to save changes. */
  managePermission: PermissionCode;
  /** When true, the channel/domain is a preview of an upcoming capability (no backend yet). */
  preview?: boolean;
  /** When true, the item is kept in code but hidden from the settings UI. */
  hiddenFromUi?: boolean;
}

function item(
  key: StoreSettingsDomain,
  label: string,
  description: string,
  segment: string,
  icon: ReactNode,
  preview = false,
  hiddenFromUi = false,
): SettingsNavItem {
  return {
    key,
    label,
    description,
    segment,
    path: `/settings/${segment}`,
    icon,
    viewPermission: STORE_SETTINGS_PERMISSIONS[key].view,
    managePermission: STORE_SETTINGS_PERMISSIONS[key].manage,
    preview,
    hiddenFromUi,
  };
}

/**
 * Ordered navigation model for every settings domain.
 *
 * `label`/`description` arguments are i18next KEYS (e.g. 'settings:nav.items.delivery.label'),
 * not literal display text — consumers must render `t(nav.label)` / `t(nav.description)`.
 * Translations live in `public/locales/{lng}/settings.json` under the `nav.items` key.
 */
export const SETTINGS_NAV: SettingsNavItem[] = [
  item(
    'delivery',
    'settings:nav.items.delivery.label',
    'settings:nav.items.delivery.description',
    'delivery',
    <LocalShippingRoundedIcon />,
  ),
  item(
    'payments',
    'settings:nav.items.payments.label',
    'settings:nav.items.payments.description',
    'payments',
    <CreditCardRoundedIcon />,
  ),
  item(
    'checkout',
    'settings:nav.items.checkout.label',
    'settings:nav.items.checkout.description',
    'checkout',
    <ShoppingCartCheckoutRoundedIcon />,
  ),
  item(
    'orders',
    'settings:nav.items.orders.label',
    'settings:nav.items.orders.description',
    'orders',
    <ReceiptLongRoundedIcon />,
  ),
  item(
    'pricing',
    'settings:nav.items.pricing.label',
    'settings:nav.items.pricing.description',
    'pricing',
    <SellRoundedIcon />,
  ),
  item(
    'catalog',
    'settings:nav.items.catalog.label',
    'settings:nav.items.catalog.description',
    'catalog',
    <Inventory2RoundedIcon />,
  ),
  item(
    'languages',
    'settings:nav.items.languages.label',
    'settings:nav.items.languages.description',
    'languages',
    <TranslateRoundedIcon />,
  ),
  item(
    'customers',
    'settings:nav.items.customers.label',
    'settings:nav.items.customers.description',
    'customers',
    <GroupsRoundedIcon />,
  ),
  item(
    'notifications',
    'settings:nav.items.notifications.label',
    'settings:nav.items.notifications.description',
    'notifications',
    <NotificationsRoundedIcon />,
  ),
  item(
    'content',
    'settings:nav.items.content.label',
    'settings:nav.items.content.description',
    'content',
    <ArticleRoundedIcon />,
  ),
  item(
    'engagement',
    'settings:nav.items.engagement.label',
    'settings:nav.items.engagement.description',
    'engagement',
    <CampaignRoundedIcon />,
  ),
  item(
    'channels',
    'settings:nav.items.channels.label',
    'settings:nav.items.channels.description',
    'channels',
    <HubRoundedIcon />,
  ),
  item(
    'telegram',
    'settings:nav.items.telegram.label',
    'settings:nav.items.telegram.description',
    'telegram',
    <TelegramIcon />,
  ),
  item(
    'max',
    'settings:nav.items.max.label',
    'settings:nav.items.max.description',
    'max',
    <ChatRoundedIcon />,
  ),
  item(
    'retail-evotor',
    'settings:nav.items.retail.label',
    'settings:nav.items.retail.description',
    'retail',
    <PointOfSaleRoundedIcon />,
  ),
  item(
    'web',
    'settings:nav.items.web.label',
    'settings:nav.items.web.description',
    'web',
    <LanguageRoundedIcon />,
    true,
    true,
  ),
  item(
    'ozon',
    'settings:nav.items.ozon.label',
    'settings:nav.items.ozon.description',
    'ozon',
    <ShoppingBagRoundedIcon />,
    true,
    true,
  ),
  item(
    'wildberries',
    'settings:nav.items.wildberries.label',
    'settings:nav.items.wildberries.description',
    'wildberries',
    <LocalMallRoundedIcon />,
    true,
    true,
  ),
  item(
    'yandex-market',
    'settings:nav.items.yandexMarket.label',
    'settings:nav.items.yandexMarket.description',
    'yandex-market',
    <ShoppingCartRoundedIcon />,
    true,
    true,
  ),
  item(
    'lamoda',
    'settings:nav.items.lamoda.label',
    'settings:nav.items.lamoda.description',
    'lamoda',
    <CheckroomRoundedIcon />,
    true,
    true,
  ),
  item(
    'tenant',
    'settings:nav.items.tenant.label',
    'settings:nav.items.tenant.description',
    'tenant',
    <DomainRoundedIcon />,
  ),
  item(
    'store',
    'settings:nav.items.store.label',
    'settings:nav.items.store.description',
    'store',
    <StoreRoundedIcon />,
  ),
  item(
    'address',
    'settings:nav.items.address.label',
    'settings:nav.items.address.description',
    'address',
    <PlaceRoundedIcon />,
  ),
  item(
    'inventory',
    'settings:nav.items.inventory.label',
    'settings:nav.items.inventory.description',
    'inventory',
    <WarehouseRoundedIcon />,
  ),
  item(
    'media',
    'settings:nav.items.media.label',
    'settings:nav.items.media.description',
    'media',
    <PermMediaRoundedIcon />,
  ),
  // The chatbot behaviour screen (plan-merchant-portal-019 WP2 = plan-conversation-001 WP-MP.4):
  // General → per-channel greeting/commands/AI/hand-off, absorbing the former standalone
  // `ai-agents` page as its AI provider card (`#ai` anchor).
  item(
    'chatbot',
    'settings:nav.items.chatbot.label',
    'settings:nav.items.chatbot.description',
    'chatbot',
    <SmartToyRoundedIcon />,
  ),
  // The chatbot templates & FAQ editor (plan-conversation-001 WP-MP.2). Segment is two levels deep
  // (`chatbot/templates`) — `item()`'s `path = /settings/${segment}` still resolves correctly since
  // react-router accepts a multi-segment relative `path` on a single leaf route (see
  // `settingsRoutes`'s `integrations/evotor` redirect for the same shape).
  item(
    'chatbot-templates',
    'settings:nav.items.chatbotTemplates.label',
    'settings:nav.items.chatbotTemplates.description',
    'chatbot/templates',
    <ForumRoundedIcon />,
  ),
  item(
    'basket',
    'settings:nav.items.basket.label',
    'settings:nav.items.basket.description',
    'basket',
    <ShoppingBasketRoundedIcon />,
  ),
  item(
    'erp-posiflora',
    'settings:nav.items.erp.label',
    'settings:nav.items.erp.description',
    'erp',
    <CloudSyncRoundedIcon />,
  ),
  // `evotor-integration` (bare `integrations.evotor.*` codes) deliberately has NO nav item of its
  // own any more — it is the Automation tab on the `retail-evotor` page now, gated
  // independently there via `useDomainPermissions('evotor-integration')`. It stays a real entry in
  // `STORE_SETTINGS_PERMISSIONS`/`useDomainPermissions` (that tab still needs it); it is just
  // excluded from `SETTINGS_NAV`/`SETTINGS_GROUPS`, mirroring how `general` is excluded above —
  // see `settingsNav.test.ts`'s `HIDDEN_PAGE_KEYS`.
];

/** Fast lookup of a settings nav item by its domain key. */
export const SETTINGS_NAV_BY_KEY = Object.fromEntries(
  SETTINGS_NAV.map((nav) => [nav.key, nav]),
) as Record<StoreSettingsDomain, SettingsNavItem>;

export interface SettingsGroup {
  /** Stable group key. */
  key: string;
  /** Group heading shown in the settings rail and overview. i18next key — render with `t(label)`. */
  label: string;
  /** One-line description for the overview group header. i18next key — render with `t(description)`. */
  description: string;
  /** Group icon. */
  icon: ReactNode;
  /** Ordered settings domains that belong to this group. */
  domains: StoreSettingsDomain[];
  /** Optional feature flag key that can hide the whole group from the UI. */
  featureKey?: MerchantPortalSettingsSectionKey;
}

/**
 * Grouped settings information architecture.
 *
 * The 19 settings domains are organised into a handful of meaningful categories
 * so the settings area is scannable instead of a flat wall of tiles. Every domain
 * appears in exactly one group; the order here drives both the settings rail and
 * the grouped overview.
 *
 * `label`/`description` are i18next KEYS, not literal text — render with `t(group.label)` /
 * `t(group.description)`. Translations live in `public/locales/{lng}/settings.json` under
 * the `nav.groups` key.
 */
export const SETTINGS_GROUPS: SettingsGroup[] = [
  {
    key: 'store',
    label: 'settings:nav.groups.store.label',
    description: 'settings:nav.groups.store.description',
    icon: <StorefrontRoundedIcon />,
    domains: ['tenant'],
  },
  {
    key: 'sales-channels',
    label: 'settings:nav.groups.salesChannels.label',
    description: 'settings:nav.groups.salesChannels.description',
    icon: <ForumRoundedIcon />,
    domains: [
      // The unified channel registry (adr-tenant-001) — the identity home; the rest of the group
      // are per-touchpoint/per-integration settings screens.
      'channels',
      'telegram',
      'max',
      'retail-evotor',
      'web',
      'ozon',
      'wildberries',
      'yandex-market',
      'lamoda',
    ],
    featureKey: 'salesChannels',
  },
  // plan-conversation-001 WP-MP.2/WP-MP.4 (adr-merchant-portal-019 D2/D3/D5): the `chatbot` rail
  // group, placed where the retired `ai` group used to sit — the AI provider page absorbed into
  // this domain's `#ai` card (plan-merchant-portal-019 WP2) is what let `ai`/`ai-agents` retire.
  {
    key: 'chatbot',
    label: 'settings:nav.groups.chatbot.label',
    description: 'settings:nav.groups.chatbot.description',
    icon: <SmartToyRoundedIcon />,
    domains: ['chatbot', 'chatbot-templates'],
    featureKey: 'chatbot',
  },
  {
    key: 'customers',
    label: 'settings:nav.groups.customersMarketing.label',
    description: 'settings:nav.groups.customersMarketing.description',
    icon: <GroupsRoundedIcon />,
    domains: ['customers', 'notifications', 'content', 'engagement'],
    featureKey: 'customersMarketing',
  },
  {
    key: 'selling',
    label: 'settings:nav.groups.selling.label',
    description: 'settings:nav.groups.selling.description',
    icon: <CreditCardRoundedIcon />,
    domains: ['payments', 'checkout', 'basket', 'pricing'],
    featureKey: 'selling',
  },
  {
    key: 'fulfillment',
    label: 'settings:nav.groups.fulfillment.label',
    description: 'settings:nav.groups.fulfillment.description',
    icon: <LocalShippingRoundedIcon />,
    // Posiflora is a broad ERP/operations integration (catalog + inventory + price + store +
    // orders + payments + returns sync) — closer to fulfilment/operations than to a literal
    // outward sales channel (unlike Evotor, which also *sells through* the POS terminal itself
    // and so lives in `sales-channels`).
    domains: ['delivery', 'store', 'address', 'orders', 'inventory', 'erp-posiflora'],
    featureKey: 'fulfilment',
  },
  {
    key: 'catalog',
    label: 'settings:nav.groups.catalogMedia.label',
    description: 'settings:nav.groups.catalogMedia.description',
    icon: <Inventory2RoundedIcon />,
    domains: ['catalog', 'languages', 'media'],
    featureKey: 'catalogMedia',
  },
  // The `integrations` group (formerly just `evotor-integration`'s standalone nav entry) is gone:
  // that domain merged into the `retail-evotor` page as its Automation tab (see the comment
  // by `evotor-integration`'s removed `item()` call above), leaving this group with zero domains.
  // Removed rather than left empty — `SettingsLayout`/`SettingsIndexPage` filter empty groups out
  // at render time, but `settingsNav.test.ts` asserts every *domain* belongs to exactly one group,
  // which a zero-domain group trivially satisfies vacuously; keeping a phantom group around would
  // just be dead code. Its now-unused `featureKey: 'integrations'` was also removed from
  // `shared/navigation/merchantPortalNavVisibility.ts` (nothing else referenced it).
];

/** Resolve a group's domains to their (ordered) settings nav items. */
export function settingsNavForGroup(group: SettingsGroup): SettingsNavItem[] {
  return group.domains.map((domain) => SETTINGS_NAV_BY_KEY[domain]);
}
